/**
 * OceanCascade.ts — owns the textures and pipelines for a single FFT cascade.
 *
 * Resources per cascade:
 *   - h0Tex      : rgba32float (h0(k), conj(h0(-k))) — written once per wind change
 *   - hSpec / dxSpec / dzSpec : rg32float spectra at time t (after fftshift)
 *   - scratch    : rg32float, ping-pong scratch for FFT (row-pass output)
 *   - hOut / dxOut / dzOut : rg32float, real-space displacement (column-pass output)
 *
 * Public methods:
 *   - encodeInit(encoder, params) — recompute h0 (call when wind changes)
 *   - encodeUpdate(encoder, time) — per-frame: evolve spectrum + run IFFTs
 *   - sampledViews              — for binding into the water shader
 */

import spectrumInitWgsl from "../../shaders/spectrum_init.compute.wgsl?raw";
import spectrumUpdateWgsl from "../../shaders/spectrum_update.compute.wgsl?raw";
import { createUniformBuffer } from "../../gpu/Buffers";
import { FFT2D, FFT_N } from "./FFT2D";

export interface CascadeWindParams {
  /** Wind direction unit vector (xz). */
  windDir: [number, number];
  /** Wind speed at 10 m, m/s. */
  windSpeed: number;
  /** Optional multiplier on the cascade amplitude constant. Default 1. */
  amplitudeScale?: number;
}

export interface CascadeConfig {
  /** Tile world size in metres. */
  L: number;
  /** Base spectrum amplitude constant. Tune per-cascade. */
  amplitude: number;
  /** |dot(khat, wind)|^p exponent. Higher = stronger directionality. */
  directionality: number;
  /** Small-wave cutoff (metres). */
  smallWaveCutoff: number;
  /** Representative local depth for TMA attenuation (m). */
  depthM: number;
  /** Representative fetch length for peak shaping (m). */
  fetchM: number;
  /** JONSWAP peak enhancement factor (typically 1.0..3.3). */
  gammaPeak: number;
  /** Random seed (uint32). */
  seed: number;
  /** Optional debug label suffix. */
  label?: string;
}

export class OceanCascade {
  readonly N = FFT_N;
  readonly config: CascadeConfig;

  private h0Tex: GPUTexture;
  private hSpecTex: GPUTexture;
  private dxSpecTex: GPUTexture;
  private dzSpecTex: GPUTexture;
  private scratchTex: GPUTexture;
  private hOutTex: GPUTexture;
  private dxOutTex: GPUTexture;
  private dzOutTex: GPUTexture;

  private initParams: GPUBuffer;
  private updateParams: GPUBuffer;

  private initPipeline: GPUComputePipeline;
  private updatePipeline: GPUComputePipeline;
  private initBindGroup: GPUBindGroup;
  private updateBindGroup: GPUBindGroup;

  private fft: FFT2D;

  constructor(
    private device: GPUDevice,
    config: CascadeConfig,
  ) {
    this.config = config;
    const N = this.N;
    const tag = config.label ?? `L=${config.L}`;

    const make = (label: string, format: GPUTextureFormat): GPUTexture =>
      device.createTexture({
        label: `Ocean[${tag}].${label}`,
        size: { width: N, height: N },
        format,
        usage:
          GPUTextureUsage.TEXTURE_BINDING |
          GPUTextureUsage.STORAGE_BINDING |
          GPUTextureUsage.COPY_SRC |
          GPUTextureUsage.COPY_DST,
      });

    this.h0Tex = make("h0", "rgba32float");
    this.hSpecTex = make("hSpec", "rg32float");
    this.dxSpecTex = make("dxSpec", "rg32float");
    this.dzSpecTex = make("dzSpec", "rg32float");
    this.scratchTex = make("scratch", "rg32float");
    this.hOutTex = make("hOut", "rg32float");
    this.dxOutTex = make("dxOut", "rg32float");
    this.dzOutTex = make("dzOut", "rg32float");

    // ---------------- Spectrum init pipeline -----------------
    this.initParams = createUniformBuffer(
      device,
      64,
      `Ocean[${tag}].initParams`,
    );
    {
      const module = device.createShaderModule({
        label: "spectrum_init.compute.wgsl",
        code: spectrumInitWgsl,
      });
      const bgl = device.createBindGroupLayout({
        label: `Ocean[${tag}].init.bgl`,
        entries: [
          {
            binding: 0,
            visibility: GPUShaderStage.COMPUTE,
            buffer: { type: "uniform" },
          },
          {
            binding: 1,
            visibility: GPUShaderStage.COMPUTE,
            storageTexture: {
              access: "write-only",
              format: "rgba32float",
            },
          },
        ],
      });
      this.initPipeline = device.createComputePipeline({
        label: `Ocean[${tag}].init.pipeline`,
        layout: device.createPipelineLayout({ bindGroupLayouts: [bgl] }),
        compute: { module, entryPoint: "main" },
      });
      this.initBindGroup = device.createBindGroup({
        label: `Ocean[${tag}].init.bg`,
        layout: bgl,
        entries: [
          { binding: 0, resource: { buffer: this.initParams } },
          { binding: 1, resource: this.h0Tex.createView() },
        ],
      });
    }

    // ---------------- Spectrum update pipeline ----------------
    this.updateParams = createUniformBuffer(
      device,
      16,
      `Ocean[${tag}].updateParams`,
    );
    {
      const module = device.createShaderModule({
        label: "spectrum_update.compute.wgsl",
        code: spectrumUpdateWgsl,
      });
      const bgl = device.createBindGroupLayout({
        label: `Ocean[${tag}].update.bgl`,
        entries: [
          {
            binding: 0,
            visibility: GPUShaderStage.COMPUTE,
            buffer: { type: "uniform" },
          },
          {
            binding: 1,
            visibility: GPUShaderStage.COMPUTE,
            texture: { sampleType: "unfilterable-float" },
          },
          {
            binding: 2,
            visibility: GPUShaderStage.COMPUTE,
            storageTexture: { access: "write-only", format: "rg32float" },
          },
          {
            binding: 3,
            visibility: GPUShaderStage.COMPUTE,
            storageTexture: { access: "write-only", format: "rg32float" },
          },
          {
            binding: 4,
            visibility: GPUShaderStage.COMPUTE,
            storageTexture: { access: "write-only", format: "rg32float" },
          },
        ],
      });
      this.updatePipeline = device.createComputePipeline({
        label: `Ocean[${tag}].update.pipeline`,
        layout: device.createPipelineLayout({ bindGroupLayouts: [bgl] }),
        compute: { module, entryPoint: "main" },
      });
      this.updateBindGroup = device.createBindGroup({
        label: `Ocean[${tag}].update.bg`,
        layout: bgl,
        entries: [
          { binding: 0, resource: { buffer: this.updateParams } },
          { binding: 1, resource: this.h0Tex.createView() },
          { binding: 2, resource: this.hSpecTex.createView() },
          { binding: 3, resource: this.dxSpecTex.createView() },
          { binding: 4, resource: this.dzSpecTex.createView() },
        ],
      });
    }

    this.fft = new FFT2D(device, /* inverse */ true);
  }

  /** Re-initialize h0(k) from current wind. Call once per wind change. */
  encodeInit(encoder: GPUCommandEncoder, wind: CascadeWindParams): void {
    // Layout: u32 N, u32 _pad, f32 L, f32 amplitude,
    //         vec2<f32> windDir, f32 windSpeed, f32 directionality,
    //         f32 smallWaveCutoff, u32 seed, f32 depthM, f32 fetchM,
    //         f32 gammaPeak, f32 _pad, f32 _pad, f32 _pad
    const buf = new ArrayBuffer(64);
    const u32 = new Uint32Array(buf);
    const f32 = new Float32Array(buf);
    u32[0] = this.N;
    u32[1] = 0;
    f32[2] = this.config.L;
    f32[3] = this.config.amplitude * (wind.amplitudeScale ?? 1.0);
    f32[4] = wind.windDir[0];
    f32[5] = wind.windDir[1];
    f32[6] = wind.windSpeed;
    f32[7] = this.config.directionality;
    f32[8] = this.config.smallWaveCutoff;
    u32[9] = this.config.seed;
    f32[10] = this.config.depthM;
    f32[11] = this.config.fetchM;
    f32[12] = this.config.gammaPeak;
    f32[13] = 0;
    f32[14] = 0;
    f32[15] = 0;
    this.device.queue.writeBuffer(this.initParams, 0, buf);

    const pass = encoder.beginComputePass({ label: "Ocean.spectrumInit" });
    pass.setPipeline(this.initPipeline);
    pass.setBindGroup(0, this.initBindGroup);
    const wg = Math.ceil(this.N / 16);
    pass.dispatchWorkgroups(wg, wg, 1);
    pass.end();
  }

  /** Per-frame: evolve in time, then 3× IFFT. */
  encodeUpdate(encoder: GPUCommandEncoder, time: number): void {
    const buf = new ArrayBuffer(16);
    const u32 = new Uint32Array(buf);
    const f32 = new Float32Array(buf);
    u32[0] = this.N;
    u32[1] = 0;
    f32[2] = this.config.L;
    // Wrap time to keep cosines well-conditioned (fundamental period of any
    // single mode is 2π/ω; we wrap to 1000 s which is much larger but bounded).
    f32[3] = time % 1000;
    this.device.queue.writeBuffer(this.updateParams, 0, buf);

    {
      const pass = encoder.beginComputePass({
        label: "Ocean.spectrumUpdate",
      });
      pass.setPipeline(this.updatePipeline);
      pass.setBindGroup(0, this.updateBindGroup);
      const wg = Math.ceil(this.N / 16);
      pass.dispatchWorkgroups(wg, wg, 1);
      pass.end();
    }

    // 3× 2D IFFT.
    const ifft = (specTex: GPUTexture, outTex: GPUTexture): void => {
      this.fft.encode(encoder, {
        inputView: specTex.createView(),
        scratchView: this.scratchTex.createView(),
        scratchSampledView: this.scratchTex.createView(),
        outputView: outTex.createView(),
      });
    };
    ifft(this.hSpecTex, this.hOutTex);
    ifft(this.dxSpecTex, this.dxOutTex);
    ifft(this.dzSpecTex, this.dzOutTex);
  }

  /** Sampled views for the water render pass. Real part of complex IFFT
   *  outputs is the displacement; the .r channel holds it. */
  views(): {
    hOut: GPUTextureView;
    dxOut: GPUTextureView;
    dzOut: GPUTextureView;
  } {
    return {
      hOut: this.hOutTex.createView(),
      dxOut: this.dxOutTex.createView(),
      dzOut: this.dzOutTex.createView(),
    };
  }

  destroy(): void {
    this.h0Tex.destroy();
    this.hSpecTex.destroy();
    this.dxSpecTex.destroy();
    this.dzSpecTex.destroy();
    this.scratchTex.destroy();
    this.hOutTex.destroy();
    this.dxOutTex.destroy();
    this.dzOutTex.destroy();
    this.initParams.destroy();
    this.updateParams.destroy();
    this.fft.destroy();
  }
}
